import { useMemo, useState } from 'react';
import { Boxes, Camera, HandHelping, Info, MapPin, ShieldAlert, Wrench } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/Dialog';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/Sheet';
import { useSignedUrl } from '@/features/workMode/hooks';
import { useItems } from '@/features/inventory/hooks';
import {
  useStationParts,
  useStationPhotos,
  useStationTools,
} from '@/features/workMode/stationSetup';
import type { FloorWorkstation, WorkstationPhoto } from '@/features/workMode/types';

interface WorkStationInfoSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  station: FloorWorkstation;
  /** "I need a tool" shortcut — opens the request dialog. */
  onRequest?: () => void;
}

/**
 * "Station" on the bench: everything the manager set up for this bench,
 * read-only and at tablet scale. What it should look like (photos), what
 * to wear and watch for, the tools and parts that have to be here, where
 * things are, and any notes. The worker can tick tools and parts off as
 * a pre-shift check; the ticks are not saved.
 */
export function WorkStationInfoSheet({
  open,
  onOpenChange,
  station,
  onRequest,
}: WorkStationInfoSheetProps) {
  const { data: items = [] } = useItems();
  const skuOf = useMemo(() => new Map(items.map((i) => [i.id, i.sku])), [items]);
  const { data: photos = [], isLoading: photosLoading } = useStationPhotos(
    open ? station.id : null,
  );
  const { data: tools = [], isLoading: toolsLoading } = useStationTools(open ? station.id : null);
  const { data: parts = [], isLoading: partsLoading } = useStationParts(open ? station.id : null);
  const [lightbox, setLightbox] = useState<WorkstationPhoto | null>(null);
  const [ticked, setTicked] = useState<Set<string>>(() => new Set());

  const toggle = (id: string) => {
    setTicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const loading = photosLoading || toolsLoading || partsLoading;
  const hasSafety = station.ppe.length > 0 || !!station.safety_notes;
  const empty =
    !loading &&
    photos.length === 0 &&
    tools.length === 0 &&
    parts.length === 0 &&
    !hasSafety &&
    !station.location_notes &&
    !station.setup_notes;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <SheetHeader className="pr-12">
          <SheetTitle className="flex items-center gap-2">
            <Info className="h-5 w-5 text-info" />
            {station.name}
          </SheetTitle>
          <SheetDescription>
            {station.description || 'How this station should be set up.'}
          </SheetDescription>
        </SheetHeader>
        <SheetBody className="space-y-6">
          {loading && (
            <div className="space-y-3">
              <Skeleton className="h-40 rounded-2xl" />
              <Skeleton className="h-24 rounded-2xl" />
            </div>
          )}

          {empty && (
            <div className="rounded-2xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
              Nothing has been written up for this station yet. Ask a manager to fill in the station
              setup.
            </div>
          )}

          {hasSafety && (
            <Section icon={ShieldAlert} title="Before you start" tone="warn">
              {station.ppe.length > 0 && (
                <ul className="flex flex-wrap gap-2">
                  {station.ppe.map((p) => (
                    <li
                      key={p}
                      className="rounded-full border border-fresh-salmon/60 bg-card px-3 py-1.5 text-sm font-medium text-fresh-salmon-dark"
                    >
                      {p}
                    </li>
                  ))}
                </ul>
              )}
              {station.safety_notes && (
                <p className="whitespace-pre-line text-sm text-foreground">
                  {station.safety_notes}
                </p>
              )}
            </Section>
          )}

          {photos.length > 0 && (
            <Section icon={Camera} title="What it should look like">
              <ul className="grid grid-cols-2 gap-2">
                {photos.map((p) => (
                  <li key={p.id}>
                    <button
                      type="button"
                      onClick={() => setLightbox(p)}
                      className="block w-full overflow-hidden rounded-xl border border-border bg-secondary text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <div className="aspect-[4/3]">
                        <Photo path={p.image_path} alt={p.caption ?? 'Station photo'} />
                      </div>
                      {p.caption && (
                        <p className="truncate px-2 py-1.5 text-xs text-muted-foreground">
                          {p.caption}
                        </p>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {tools.length > 0 && (
            <Section icon={Wrench} title="Tools at this bench" hint="Tap to tick off">
              <ul className="divide-y divide-border rounded-xl border border-border">
                {tools.map((t) => (
                  <li key={t.id}>
                    <CheckRow
                      checked={ticked.has(t.id)}
                      onToggle={() => toggle(t.id)}
                      title={`${t.quantity > 1 ? `${t.quantity} × ` : ''}${t.name}`}
                      sub={
                        [t.is_required ? null : 'Nice to have', t.notes]
                          .filter(Boolean)
                          .join(' · ') || null
                      }
                    />
                  </li>
                ))}
              </ul>
              {onRequest && (
                <Button variant="outline" size="lg" className="w-full" onClick={onRequest}>
                  <HandHelping className="h-5 w-5" />
                  Something is missing — request it
                </Button>
              )}
            </Section>
          )}

          {parts.length > 0 && (
            <Section icon={Boxes} title="Parts to have stocked" hint="Tap to tick off">
              <ul className="divide-y divide-border rounded-xl border border-border">
                {parts.map((p) => {
                  const qty =
                    p.quantity === null ? null : `${p.quantity}${p.uom ? ` ${p.uom}` : ''}`;
                  return (
                    <li key={p.id}>
                      <CheckRow
                        checked={ticked.has(p.id)}
                        onToggle={() => toggle(p.id)}
                        title={p.name}
                        sub={
                          [
                            qty ? `Keep ${qty}` : null,
                            p.bin_location ? `Bin ${p.bin_location}` : null,
                            p.item_id ? (skuOf.get(p.item_id) ?? null) : null,
                            p.notes,
                          ]
                            .filter(Boolean)
                            .join(' · ') || null
                        }
                      />
                    </li>
                  );
                })}
              </ul>
            </Section>
          )}

          {station.location_notes && (
            <Section icon={MapPin} title="Where things are">
              <p className="whitespace-pre-line text-sm text-foreground">
                {station.location_notes}
              </p>
            </Section>
          )}

          {station.setup_notes && (
            <Section icon={Info} title="Good to know">
              <p className="whitespace-pre-line text-sm text-foreground">{station.setup_notes}</p>
            </Section>
          )}
        </SheetBody>
      </SheetContent>

      <Dialog open={!!lightbox} onOpenChange={(o) => !o && setLightbox(null)}>
        <DialogContent className="max-w-4xl p-2">
          <DialogTitle className="sr-only">Station photo</DialogTitle>
          {lightbox && (
            <div>
              <div className="max-h-[80vh]">
                <Photo
                  path={lightbox.image_path}
                  alt={lightbox.caption ?? 'Station photo'}
                  contain
                />
              </div>
              {lightbox.caption && (
                <p className="px-2 pb-1 pt-2 text-center text-sm text-muted-foreground">
                  {lightbox.caption}
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Sheet>
  );
}

function Section({
  icon: Icon,
  title,
  hint,
  tone,
  children,
}: {
  icon: typeof Info;
  title: string;
  hint?: string;
  tone?: 'warn';
  children: React.ReactNode;
}) {
  return (
    <section
      className={cn(
        'space-y-3 rounded-2xl border p-4',
        tone === 'warn' ? 'border-fresh-salmon/60 bg-fresh-salmon/10' : 'border-border bg-card',
      )}
    >
      <header className="flex items-center justify-between gap-2">
        <h3
          className={cn(
            'flex items-center gap-2 text-sm font-medium',
            tone === 'warn' ? 'text-fresh-salmon-dark' : 'text-foreground',
          )}
        >
          <Icon className="h-4 w-4" />
          {title}
        </h3>
        {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
      </header>
      {children}
    </section>
  );
}

function CheckRow({
  checked,
  onToggle,
  title,
  sub,
}: {
  checked: boolean;
  onToggle: () => void;
  title: string;
  sub: string | null;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex min-h-[52px] w-full items-center gap-3 px-3 py-2 text-left hover:bg-secondary/60"
      aria-pressed={checked}
    >
      <span
        className={cn(
          'flex h-6 w-6 shrink-0 items-center justify-center rounded-md border-2 transition-colors',
          checked
            ? 'border-bright-moss-dark bg-bright-moss-dark text-linen'
            : 'border-border bg-card',
        )}
      >
        {checked && (
          <svg
            viewBox="0 0 16 16"
            className="h-4 w-4"
            fill="none"
            stroke="currentColor"
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M3 8.5l3 3 7-7" />
          </svg>
        )}
      </span>
      <span className="min-w-0 flex-1">
        <span
          className={cn(
            'block text-sm font-medium',
            checked ? 'text-muted-foreground line-through' : 'text-foreground',
          )}
        >
          {title}
        </span>
        {sub && <span className="block truncate text-xs text-muted-foreground">{sub}</span>}
      </span>
    </button>
  );
}

function Photo({ path, alt, contain }: { path: string; alt: string; contain?: boolean }) {
  const { data: url, isLoading } = useSignedUrl('work-instructions', path);
  if (isLoading || !url) return <Skeleton className="h-full w-full rounded-none" />;
  return (
    <img
      src={url}
      alt={alt}
      className={cn('h-full w-full', contain ? 'max-h-[80vh] object-contain' : 'object-cover')}
      draggable={false}
    />
  );
}
